import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { AgentAddress, AgentGrid, AgentInfo } from '../agent.model';
import { AgentService } from 'src/app/features/service/agent.service';
import {
  CreateAgent,
  DeleteAgent,
  FetchAgentInfo,
  FetchAgents,
  InitializeAgentDropdowns,
  SetAgents,
  UpdateAgent,
  UpdateAgentFilters,
} from './agent.action';
import { combineLatestWith, mergeMap, tap } from 'rxjs';
import { CommonService } from '../../../../shared/services/common.service';
import { MasterLookup } from '../../../../shared/utility/constants';
import { FetchDropdowns } from '../../hawb/store/hawb.action';

@State<AgentGrid>({
  name: 'agentSummary',
  defaults: {
    searchText: '',
    agents: [],
    totalRecords: 0,
    paging: {
      sortingOrder: 'desc',
      sortingField: 'id',
      fromIndex: 0,
      toIndex: 10,
    },
  },
})
@Injectable()
export class AgentSummaryState {
  private readonly agentService: AgentService = inject(AgentService);

  @Action(SetAgents)
  setAgents(ctx: StateContext<AgentGrid>, action: SetAgents) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      ...action.payload,
    });
  }

  @Action(FetchAgents)
  fetchAgents(ctx: StateContext<AgentGrid>, action: FetchAgents) {
    return this.agentService.getSearchData(action.payload).pipe(
      tap(agentResponse => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          ...agentResponse,
        });
      })
    );
  }

  @Action(UpdateAgentFilters)
  updateFilters(ctx: StateContext<AgentGrid>, action: UpdateAgentFilters) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      ...action.payload,
    });

    return ctx.dispatch(
      new FetchAgents({
        searchText: ctx.getState().searchText,
        paging: ctx.getState().paging,
      })
    );
  }
}

@State<Partial<AgentInfo>>({
  name: 'agentInformation',
  defaults: {
    agent: {} as AgentInfo['agent'],
    address: {} as AgentAddress,
    agentTypes: [],
  },
})
@Injectable()
export class AgentInformationState {
  private readonly agentService: AgentService = inject(AgentService);
  private readonly commonService: CommonService = inject(CommonService);

  @Action(FetchAgentInfo)
  fetchAgentInfo(ctx: StateContext<AgentInfo>, action: FetchAgentInfo) {
    return this.agentService.getById(action.id).pipe(
      tap(agentResponse => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          ...agentResponse,
        });
      })
    );
  }

  @Action(InitializeAgentDropdowns)
  initializeFormDropdown(ctx: StateContext<AgentInfo>) {
    return this.commonService.getMasterLookUp(MasterLookup.countries).pipe(
      combineLatestWith(
        this.commonService.getMasterLookUp(MasterLookup.agentTypes)
      ),
      tap(([_, types]) => {
        ctx.setState({
          ...ctx.getState(),
          agentTypes: types.data,
        });
      }),
      mergeMap(([countries]) =>
        ctx.dispatch(new FetchDropdowns({ countries: countries.data ?? [] }))
      )
    );
  }

  @Action(CreateAgent)
  createAgent(ctx: StateContext<AgentInfo>, action: CreateAgent) {
    return this.agentService.create(action.payload).pipe(
      tap(id =>
        ctx.patchState({
          agent: {
            ...ctx.getState().agent,
            id,
          },
        })
      )
    );
  }

  @Action(UpdateAgent)
  update(_: StateContext<AgentInfo>, action: UpdateAgent) {
    return this.agentService.update(action.payload);
  }

  @Action(DeleteAgent)
  delete(_: StateContext<AgentInfo>, action: DeleteAgent) {
    return this.agentService.delete(action.ids);
  }
}
